import { apiUrl } from './api-client'
import { AUTH_ERROR_CODES } from './auth-client'
import type { RequestOptions } from './api-client'
import type { AuthErrorCode, AuthFailure } from './auth-client'

/**
 * Getypeerde toegang tot de `/residences/:id/theme`-endpoints van de platform-API.
 *
 * De huisstijl van een residence is **configuratie, geen code** (§4.7, §4.8):
 * één gedeelde applicatie leest deze waarden en presenteert zich als het merk van
 * die residence. Daarom is dit een afgebakende verzameling waarden — vijf kleuren
 * en vijf keuzes — en nadrukkelijk geen vrij CSS- of stijlveld. Dat laatste zou
 * code zijn, niet te valideren, en een injectievector op elke pagina waar het
 * belandt (zie de toelichting in migratie `0010_residence_theme.sql`).
 *
 * Drie dingen liggen hier vast, in lijn met `api-client.ts`, `auth-client.ts` en
 * `media-client.ts`:
 *
 * 1. **Eén foutafhandeling.** Een aanroep werpt nooit; alles komt terug als een
 *    `ThemeFailure` met een `kind` en — wanneer de API een herkenbare
 *    `{ error: { code, message } }` teruggaf — de foutcode uit het contract. Die
 *    codelijst is contractbreed en wordt hier niet opnieuw verzonnen maar uit
 *    `auth-client.ts` overgenomen; `ThemeFailure` is letterlijk `AuthFailure`.
 * 2. **De sessiecookie moet mee.** `siteshowroom_session` is httpOnly en gaat
 *    alleen mee met `credentials: 'include'`.
 * 3. **Kleuren worden twee keer gesloten.** De database heeft een CHECK op de
 *    hexvorm; deze laag controleert hem nóg een keer, zowel op wat binnenkomt als
 *    op wat vertrekt. Een kleur uit de database belandt in een style-attribuut van
 *    het voorbeeldweergave-blok, en dat mag nooit gebeuren met een waarde die
 *    deze controle niet gepasseerd is.
 *
 * `exchange`, `httpFailure` en de kleine hulpfuncties staan letterlijk zo in
 * `media-client.ts` en `auth-client.ts`, maar zijn daar niet geëxporteerd.
 * Verhuist dit ooit naar een gedeelde module, dan verdwijnt deze kopie mee.
 */

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/** Foutcodes zijn contractbreed; het thema kent er geen eigen. */
export type ThemeErrorCode = AuthErrorCode

/** `ApiFailure` plus de foutcode van het contract — zie `AuthFailure`. */
export type ThemeFailure = AuthFailure

export type ThemeResult<T> =
  | { readonly ok: true; readonly data: T; readonly roundTripMs: number }
  | { readonly ok: false; readonly error: ThemeFailure }

/**
 * De keuzelijsten. Elke lijst is een afspiegeling van de CHECK-constraint in
 * migratie 0010: de database laat geen andere waarde toe, dus is een afwijkende
 * waarde een contractbreuk en geen datavariatie.
 *
 * De volgorde is die van de keuzelijst op het scherm. Bij vier van de vijf is dat
 * de standaardwaarde uit de migratie eerst; bij de hoekstijl leest de reeks van
 * scherp naar rond beter dan de standaardwaarde (`soft`) vooraan.
 */
export const THEME_FONT_FAMILIES = ['system', 'serif', 'humanist', 'geometric'] as const
export type ThemeFontFamily = (typeof THEME_FONT_FAMILIES)[number]

export const THEME_HEADING_STYLES = ['regular', 'condensed', 'wide'] as const
export type ThemeHeadingStyle = (typeof THEME_HEADING_STYLES)[number]

export const THEME_BUTTON_STYLES = ['rounded', 'pill', 'square'] as const
export type ThemeButtonStyle = (typeof THEME_BUTTON_STYLES)[number]

export const THEME_CORNER_STYLES = ['sharp', 'soft', 'round'] as const
export type ThemeCornerStyle = (typeof THEME_CORNER_STYLES)[number]

export const THEME_LAYOUTS = ['premium', 'compact', 'editorial'] as const
export type ThemeLayout = (typeof THEME_LAYOUTS)[number]

export interface ResidenceTheme {
  readonly primaryColor: string
  readonly secondaryColor: string
  readonly accentColor: string
  readonly textColor: string
  readonly backgroundColor: string
  readonly fontFamily: ThemeFontFamily
  readonly headingStyle: ThemeHeadingStyle
  readonly buttonStyle: ThemeButtonStyle
  readonly cornerStyle: ThemeCornerStyle
  readonly layout: ThemeLayout
  /** Verwijst naar een media-asset; `null` zolang er geen logo gekozen is. */
  readonly logoUrl: string | null
}

/** De vijf kleurvelden, in de volgorde waarin ze op het scherm staan. */
export const THEME_COLOR_FIELDS = [
  'primaryColor',
  'secondaryColor',
  'accentColor',
  'textColor',
  'backgroundColor',
] as const
export type ThemeColorField = (typeof THEME_COLOR_FIELDS)[number]

/** De vijf keuzevelden, in de volgorde waarin ze op het scherm staan. */
export const THEME_CHOICE_FIELDS = [
  'fontFamily',
  'headingStyle',
  'buttonStyle',
  'cornerStyle',
  'layout',
] as const
export type ThemeChoiceField = (typeof THEME_CHOICE_FIELDS)[number]

/**
 * Wat er gewijzigd wordt, en niets meer. Een PATCH die alle velden herhaalt
 * overschrijft stilletjes wat iemand anders intussen heeft aangepast.
 *
 * `logoUrl` staat hier bewust niet in: dat veld is afgeleid van een media-asset
 * en wordt via de mediakant gekozen, niet als losse URL (§17.2).
 */
export interface ResidenceThemePatch {
  readonly primaryColor?: string | undefined
  readonly secondaryColor?: string | undefined
  readonly accentColor?: string | undefined
  readonly textColor?: string | undefined
  readonly backgroundColor?: string | undefined
  readonly fontFamily?: ThemeFontFamily | undefined
  readonly headingStyle?: ThemeHeadingStyle | undefined
  readonly buttonStyle?: ThemeButtonStyle | undefined
  readonly cornerStyle?: ThemeCornerStyle | undefined
  readonly layout?: ThemeLayout | undefined
}

// ---------------------------------------------------------------------------
// Kleuren: vorm en contrast
// ---------------------------------------------------------------------------

/** Precies de vorm die de CHECK-constraint in migratie 0010 toelaat. */
const HEX_COLOR = /^#[0-9a-f]{6}$/

/** Waar of de waarde exact `#rrggbb` in kleine letters is — het databasecontract. */
export function isHexColor(value: string): boolean {
  return HEX_COLOR.test(value)
}

/**
 * Brengt wat iemand intikt of plakt naar de vorm van het contract, of geeft null
 * wanneer dat niet kan.
 *
 * Toegestaan zijn `#rrggbb`, `rrggbb` en de korte schrijfwijze `#rgb`, in hoofd-
 * of kleine letters — dat is wat er uit een merkrichtlijn of een ander programma
 * geplakt wordt. Alles wat overblijft is één vorm: kleine letters, met hekje, zes
 * cijfers. Er wordt niets "dichtbij" gegokt: een waarde die geen kleur is, is
 * geen kleur.
 */
export function normalizeHexColor(value: string): string | null {
  const trimmed = value.trim().toLowerCase()
  const digits = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed
  if (/^[0-9a-f]{3}$/.test(digits)) {
    // Korte schrijfwijze: elk cijfer telt dubbel (#abc → #aabbcc).
    return `#${digits.replace(/./g, (digit) => `${digit}${digit}`)}`
  }
  if (/^[0-9a-f]{6}$/.test(digits)) {
    return `#${digits}`
  }
  return null
}

/** De AA-drempel voor gewone tekst uit WCAG 2.2, succescriterium 1.4.3. */
export const WCAG_AA_CONTRAST = 4.5

/**
 * Relatieve luminantie van één kanaal, volgens de definitie van WCAG 2.2.
 * De knik bij 0,03928 en de exponent 2,4 zijn de gamma-correctie van sRGB; ze
 * staan hier als getal omdat de norm ze zo voorschrijft.
 */
function channelLuminance(value: number): number {
  const channel = value / 255
  return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
}

/** Relatieve luminantie van een gevalideerde `#rrggbb`-kleur. */
function relativeLuminance(hex: string): number {
  const red = Number.parseInt(hex.slice(1, 3), 16)
  const green = Number.parseInt(hex.slice(3, 5), 16)
  const blue = Number.parseInt(hex.slice(5, 7), 16)
  // De wegingen komen uit de norm: het oog is het gevoeligst voor groen.
  return (
    0.2126 * channelLuminance(red) +
    0.7152 * channelLuminance(green) +
    0.0722 * channelLuminance(blue)
  )
}

/**
 * Contrastverhouding tussen twee kleuren, tussen 1 (gelijk) en 21 (zwart op wit).
 * Levert null wanneer een van beide geen geldige hexkleur is; dat is een
 * programmeerfout, geen berekening.
 *
 * De formule staat hier en niet in een bibliotheek: het zijn zeven regels, en een
 * afhankelijkheid erbij voor zeven regels is duurder dan de regels zelf.
 */
export function contrastRatio(foreground: string, background: string): number | null {
  if (!isHexColor(foreground) || !isHexColor(background)) {
    return null
  }
  const first = relativeLuminance(foreground)
  const second = relativeLuminance(background)
  const lighter = Math.max(first, second)
  const darker = Math.min(first, second)
  // De 0,05 staat voor de omgevingsreflectie van een scherm; ook die komt uit de norm.
  return (lighter + 0.05) / (darker + 0.05)
}

// ---------------------------------------------------------------------------
// Hulpfuncties (kopie uit media-client.ts; zie de kop van dit bestand)
// ---------------------------------------------------------------------------

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}

function describeCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message
  }
  return String(cause)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isThemeErrorCode(value: string): value is ThemeErrorCode {
  const known: readonly string[] = AUTH_ERROR_CODES
  return known.includes(value)
}

function isSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300
}

/** Leest `{ error: { code, message } }` uit een foutantwoord; null als die vorm ontbreekt. */
function readErrorBody(payload: unknown): { code: ThemeErrorCode; message: string } | null {
  if (!isRecord(payload)) {
    return null
  }
  const error = payload['error']
  if (!isRecord(error)) {
    return null
  }
  const code = error['code']
  if (typeof code !== 'string' || !isThemeErrorCode(code)) {
    return null
  }
  const message = error['message']
  return { code, message: typeof message === 'string' ? message : code }
}

/** Zet een niet-2xx-antwoord om naar een `ThemeFailure`, mét foutcode wanneer die er is. */
function httpFailure(
  path: string,
  status: number,
  statusText: string,
  payload: unknown,
): ThemeFailure {
  const suffix = statusText === '' ? '' : ` ${statusText}`
  const base = `${path}: HTTP ${String(status)}${suffix}`
  const body = readErrorBody(payload)
  if (body === null) {
    return { kind: 'http', status, message: base }
  }
  return { kind: 'http', status, code: body.code, message: `${base} — ${body.message}` }
}

function parseBody(raw: string): unknown {
  if (raw.trim() === '') {
    return null
  }
  try {
    return JSON.parse(raw)
  } catch {
    // De aanroeper ziet dit terug als een ontbrekende vorm; bij een foutstatus
    // is een onleesbare body te overleven.
    return undefined
  }
}

// ---------------------------------------------------------------------------
// Uitwisseling met de API
// ---------------------------------------------------------------------------

type Method = 'GET' | 'PATCH'

interface Exchange {
  readonly status: number
  readonly statusText: string
  readonly payload: unknown
  readonly roundTripMs: number
}

type ExchangeResult =
  | { readonly ok: true; readonly exchange: Exchange }
  | { readonly ok: false; readonly error: ThemeFailure }

async function exchange(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<ExchangeResult> {
  const startedAt = performance.now()

  let response: Response
  try {
    response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : { accept: 'application/json', 'content-type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
      signal: options.signal ?? null,
    })
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  let raw: string
  try {
    raw = await response.text()
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  const payload = parseBody(raw)
  if (payload === undefined && response.ok) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: invalid JSON` } }
  }

  return {
    ok: true,
    exchange: {
      status: response.status,
      statusText: response.statusText,
      payload: payload ?? null,
      roundTripMs: performance.now() - startedAt,
    },
  }
}

// ---------------------------------------------------------------------------
// Validatie van de antwoordvorm
// ---------------------------------------------------------------------------
//
// Zelfde vorm als `describeHealthProblem` in `api-client.ts`: een omschrijving
// van het eerste probleem, of null. Zo wordt een afwijkend API-antwoord
// zichtbaar in plaats van als `undefined` op het scherm te belanden — of, bij een
// kleur, als onbetrouwbare tekst in een style-attribuut.

function isOneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  const known: readonly string[] = allowed
  return typeof value === 'string' && known.includes(value)
}

function describeThemeProblem(value: unknown): string | null {
  if (!isRecord(value)) {
    return 'theme is not an object'
  }

  for (const field of THEME_COLOR_FIELDS) {
    const color = value[field]
    if (typeof color !== 'string') {
      return `missing "theme.${field}"`
    }
    // Het tweede slot. De database bewaakt de hexvorm met een CHECK; hier wordt
    // hij nog een keer gecontroleerd, want deze waarde belandt in een
    // style-attribuut van de voorbeeldweergave.
    if (!isHexColor(color)) {
      return `"theme.${field}" is not a #rrggbb colour`
    }
  }

  if (!isOneOf(value['fontFamily'], THEME_FONT_FAMILIES)) {
    return '"theme.fontFamily" is not a known font family'
  }
  if (!isOneOf(value['headingStyle'], THEME_HEADING_STYLES)) {
    return '"theme.headingStyle" is not a known heading style'
  }
  if (!isOneOf(value['buttonStyle'], THEME_BUTTON_STYLES)) {
    return '"theme.buttonStyle" is not a known button style'
  }
  if (!isOneOf(value['cornerStyle'], THEME_CORNER_STYLES)) {
    return '"theme.cornerStyle" is not a known corner style'
  }
  if (!isOneOf(value['layout'], THEME_LAYOUTS)) {
    return '"theme.layout" is not a known layout'
  }

  const logoUrl = value['logoUrl']
  if (logoUrl !== null && logoUrl !== undefined && typeof logoUrl !== 'string') {
    return '"theme.logoUrl" must be a string or null'
  }

  return null
}

function toTheme(value: Record<string, unknown>): ResidenceTheme {
  // De vorm is hierboven runtime-gecontroleerd; de casts maken dat expliciet.
  const logoUrl = value['logoUrl']
  return {
    primaryColor: value['primaryColor'] as string,
    secondaryColor: value['secondaryColor'] as string,
    accentColor: value['accentColor'] as string,
    textColor: value['textColor'] as string,
    backgroundColor: value['backgroundColor'] as string,
    fontFamily: value['fontFamily'] as ThemeFontFamily,
    headingStyle: value['headingStyle'] as ThemeHeadingStyle,
    buttonStyle: value['buttonStyle'] as ThemeButtonStyle,
    cornerStyle: value['cornerStyle'] as ThemeCornerStyle,
    layout: value['layout'] as ThemeLayout,
    logoUrl: typeof logoUrl === 'string' ? logoUrl : null,
  }
}

type ThemeEnvelope =
  | { readonly ok: true; readonly theme: ResidenceTheme }
  | { readonly ok: false; readonly error: ThemeFailure }

function readThemeEnvelope(path: string, payload: unknown): ThemeEnvelope {
  if (!isRecord(payload)) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: body is not an object` },
    }
  }
  const problem = describeThemeProblem(payload['theme'])
  if (problem !== null) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: ${problem}` } }
  }
  return { ok: true, theme: toTheme(payload['theme'] as Record<string, unknown>) }
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

function themePath(residenceId: string): string {
  return `/residences/${encodeURIComponent(residenceId)}/theme`
}

/** `GET /residences/:id/theme` */
export async function fetchTheme(
  residenceId: string,
  options: RequestOptions = {},
): Promise<ThemeResult<ResidenceTheme>> {
  const path = themePath(residenceId)
  const result = await exchange('GET', path, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  const envelope = readThemeEnvelope(path, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.theme, roundTripMs: ex.roundTripMs }
}

/**
 * `PATCH /residences/:id/theme` — alleen de velden die werkelijk veranderen.
 *
 * De kleuren gaan langs `isHexColor` voordat er iets het netwerk op gaat. Dat
 * vervángt de servercontrole niet — de API en de CHECK-constraint blijven de
 * autoriteit (CLAUDE.md regel 2) — maar het bespaart een aanvraag die gegarandeerd
 * geweigerd wordt, en het houdt de belofte overeind dat een kleur nooit
 * ongecontroleerd door deze laag heen komt.
 */
export async function updateTheme(
  residenceId: string,
  patch: ResidenceThemePatch,
  options: RequestOptions = {},
): Promise<ThemeResult<ResidenceTheme>> {
  const path = themePath(residenceId)

  const body: Record<string, unknown> = {}
  for (const field of THEME_COLOR_FIELDS) {
    const value = patch[field]
    if (value === undefined) {
      continue
    }
    if (!isHexColor(value)) {
      return {
        ok: false,
        error: {
          kind: 'http',
          code: 'validation_failed',
          message: `${path}: "${field}" is not a #rrggbb colour`,
        },
      }
    }
    body[field] = value
  }
  for (const field of THEME_CHOICE_FIELDS) {
    const value = patch[field]
    if (value !== undefined) {
      body[field] = value
    }
  }

  const result = await exchange('PATCH', path, body, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  const envelope = readThemeEnvelope(path, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.theme, roundTripMs: ex.roundTripMs }
}

/** Bevat de patch werkelijk een wijziging? Een lege PATCH is geen aanvraag waard. */
export function isEmptyPatch(patch: ResidenceThemePatch): boolean {
  for (const field of THEME_COLOR_FIELDS) {
    if (patch[field] !== undefined) {
      return false
    }
  }
  for (const field of THEME_CHOICE_FIELDS) {
    if (patch[field] !== undefined) {
      return false
    }
  }
  return true
}

/**
 * Het verschil tussen wat de server heeft en wat de eigenaar heeft gemaakt.
 * Alleen dat gaat mee in de PATCH; zie de toelichting bij `updateTheme`.
 */
export function diffTheme(server: ResidenceTheme, draft: ResidenceTheme): ResidenceThemePatch {
  const patch: {
    -readonly [K in keyof ResidenceThemePatch]: ResidenceThemePatch[K]
  } = {}
  for (const field of THEME_COLOR_FIELDS) {
    if (server[field] !== draft[field]) {
      patch[field] = draft[field]
    }
  }
  if (server.fontFamily !== draft.fontFamily) {
    patch.fontFamily = draft.fontFamily
  }
  if (server.headingStyle !== draft.headingStyle) {
    patch.headingStyle = draft.headingStyle
  }
  if (server.buttonStyle !== draft.buttonStyle) {
    patch.buttonStyle = draft.buttonStyle
  }
  if (server.cornerStyle !== draft.cornerStyle) {
    patch.cornerStyle = draft.cornerStyle
  }
  if (server.layout !== draft.layout) {
    patch.layout = draft.layout
  }
  return patch
}

// ---------------------------------------------------------------------------
// Van fout naar vertaalsleutel
// ---------------------------------------------------------------------------

/**
 * Vertaalt een mislukking naar de **sleutel** van de melding die de gebruiker
 * ziet — nooit naar tekst; user-facing tekst hoort uitsluitend in de catalogi van
 * `packages/i18n` (§17.8). Zelfde opzet als `mediaErrorMessageKey`.
 */
export function themeErrorMessageKey(failure: ThemeFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.theme.error.rejected'
    case 'not_found':
      return 'dashboard.theme.error.notFound'
    case 'forbidden':
      return 'dashboard.theme.error.forbidden'
    case 'rate_limited':
      return 'dashboard.theme.error.rateLimited'
    default:
      // invalid_credentials, mfa_required, conflict, internal, en alles zonder
      // code (netwerkfout, onleesbaar antwoord): voor de gebruiker is dat één en
      // dezelfde situatie — het lukt nu niet.
      return 'dashboard.theme.error.unavailable'
  }
}
